import { ChevronIcon } from '@/components/Dashboard/icons/ChevronIcon';
import { type ParsedLinkData } from '@/components/Header/utils/links';
import { TdsNextLink } from '@/components/Link/TdsLink';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@tallinn-arvudes/ui/atoms/collapsible';
import { Slot } from '@tallinn-arvudes/ui/atoms/slot';
import { Button } from '@tallinn-arvudes/ui/tallinn-design/Button';
import { cn } from '@tallinn-arvudes/ui/utils';
import React, { type ReactNode, useState } from 'react';
export const TopicBlockLayout = ({
  children,
  subTitle,
  title,
  extra,
  borderBottom,
  id,
  link,
  asChild = false,
}: {
  link?: ParsedLinkData | null;
  id?: string;
  extra?: string | null;
  subTitle?: string | null;
  title?: string | null;
  children: ReactNode;
  className?: string;
  borderBottom?: boolean;
  asChild?: boolean;
}) => {
  const Comp = asChild ? Slot : 'div';

  const [isOpen, setIsOpen] = useState(false);

  const isChildrenEmpty = React.Children.count(children) === 0;

  return (
    <>
      <Collapsible
        onOpenChange={!isChildrenEmpty ? setIsOpen : () => {}}
        open={isOpen}
        id={id}
        className={cn('md:hidden w-full', {
          'border-b-[1px] border-semantic-border-moderate': borderBottom,
        })}
      >
        <div className="px-6 pt-3 pb-3 bg-semantic-surface-faint relative w-full">
          <CollapsibleTrigger
            className={!isChildrenEmpty ? 'cursor-pointer' : undefined}
            asChild
          >
            <div role="button" className="flex flex-col gap-2">
              {(!!subTitle || !!link?.href) && (
                <div className="flex flex-wrap-reverse items-center">
                  {!!subTitle && (
                    <p className="text-primary tds-body-lg all-small-caps font-bold">
                      {subTitle}
                    </p>
                  )}
                  {!!link?.href && (
                    <div className="ml-auto">
                      Loe lisa:{' '}
                      <TdsNextLink external={link.newTab} href={link.href}>
                        {link.name}
                      </TdsNextLink>
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center">
                <h2
                  title={title || ''}
                  className="text-semantic-content-brand tds-heading-lg font-bold break-words pb-1"
                >
                  {title}
                </h2>
                <Button
                  title={isOpen ? 'Sulge' : 'Ava'}
                  aria-label={
                    isOpen
                      ? `Sulge "${title}" sektsioon`
                      : `Ava "${title}" sektsioon`
                  }
                  compact
                  variant="icon"
                  className={cn('ml-auto shrink-0', {
                    hidden: isChildrenEmpty,
                  })}
                >
                  <ChevronIcon
                    className={cn(
                      'ml-auto shrink-0 h-4 w-4 transition-transform ease-linear',
                      isOpen
                        ? 'origin-center rotate-180'
                        : 'origin-center rotate-0',
                    )}
                  />
                </Button>
              </div>
              {extra && <p>{extra}</p>}
            </div>
          </CollapsibleTrigger>
        </div>
        <CollapsibleContent>
          <Comp className="grid grid-cols-1 p-2">{children}</Comp>
        </CollapsibleContent>
      </Collapsible>
      <div
        id={id}
        className={cn(
          'bg-semantic-surface-faint w-full hidden justify-center md:flex',
          {
            'border-b-[1px] border-semantic-border-moderate': borderBottom,
          },
        )}
      >
        <div className="px-6 md:px-8 py-6 md:py-10 flex flex-col gap-1 md:gap-2 w-full max-w-content">
          <div className="flex flex-wrap items-center">
            {subTitle && (
              <p className="text-primary tds-body-lg all-small-caps font-bold">
                {subTitle}
              </p>
            )}
            {link && !!link?.href && (
              <div className="ml-auto">
                Loe lisa:{' '}
                <TdsNextLink external={link.newTab} href={link.href}>
                  {link.name}
                </TdsNextLink>
              </div>
            )}
          </div>
          <h2
            title={title || ''}
            className="text-semantic-content-brand tds-heading-lg font-bold break-words line-clamp-2 pb-1"
          >
            {title}
          </h2>
          {extra && <p className="xl:max-w-[66.666%]">{extra}</p>}
          {!isChildrenEmpty ? (
            <Comp className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 mt-2">
              {children}
            </Comp>
          ) : null}
        </div>
      </div>
    </>
  );
};
